import React, { Component } from "react";
import ReactGA from "react-ga";
import BannerImg from "../../assets/images/banner/travel-hospitality.png";
import IndustryVerticalPage from "../../components/industry/industry-vertical-page";

const page = {
  title: "Travel Solutions, Travel Management Solutions & Booking Engines | Avani Cimcon",
  description:
    "Travel software solutions for airlines, hotels, tour operators and travel portals. Enterprise reservation systems, CRS/OTA middleware, booking engines and AI enablement built on decades of delivery.",
  keywords:
    "Travel Solutions, Travel Management Solutions, Automated Travel Solutions, travel solution Business Travel Solutions, Online Travel Software, Travel Solutions, ISO Travel Solutions, Travel software: Providers and solutions, Travel Management Software, Advanced travel technology solution, travel software solutions provider, travel software solutions, travel industry solutions, solutions for hospitality, online travel software, online travel software solutions, enterprise software engineering, legacy modernization, AI enablement, booking engines, CRS, OTA middleware, dynamic packaging",
  canonical: "https://www.avanicimcon.com/travel-hospitality",
  ogImage: "https://www.avanicimcon.com/images/banner/travel-hospitality.png",
  hero: {
    eyebrow: "Industry Expertise",
    heading: "Travel & Hospitality",
    lead:
      "In the dynamic and highly competitive Travel Industry, our plug n play solutions help organizations expand direct distribution opportunities, control relationships, lower technology costs and strengthen marketing communication with customers and prospects.",
    image: BannerImg,
    tags: ["Travel software solutions", "OTA middleware", "Booking engines", "Dynamic packaging"],
    panelKicker: "Legacy systems. Modern enterprise delivery.",
    panelTitle: "Travel software built around CRS, booking, and distribution workflows.",
    panelText:
      "We support travel vendors, destinations, associations, agents, portals, aggregators and consolidators with enterprise systems that keep legacy platforms useful while adding modernization paths.",
    stats: [
      { value: "DXchange", label: "OTA-compliant middleware and data exchange" },
      { value: "CMS", label: "Travel portal and content management heritage" },
      { value: "BRMS", label: "Business rules that support changing travel logic" },
    ],
    primaryCta: { label: "Talk to Us", href: "/contact-us" },
    secondaryCta: { label: "See IT Consulting Services", href: "/it-consulting-services" },
  },
  overview: {
    eyebrow: "Industry Overview",
    title: "Enterprise travel workflows built for direct distribution, integration and control",
    lead:
      "Travel organizations need real-time analytics, stable reservation flows, supplier integration and flexible digital channels that can adapt without breaking the business logic already embedded in their systems.",
    paragraphs: [
      "We cater to the IT needs of travel vendors and providers, destinations, associations, agents, online travel portals, aggregators and consolidators. That domain depth matters because travel technology is not just booking software; it is a network of reservation, supplier, content, pricing and fulfillment workflows that must stay in sync.",
      "Our approach preserves the search intent and industry terminology already associated with travel solutions, travel management software and online travel software solutions, while reframing the pages around enterprise software engineering, legacy modernization and safe AI enablement.",
    ],
    callout: {
      title: "What this page should prove",
      text: "Travel buyers should be able to see that Avani Cimcon understands the operational reality behind CRS, inventory, booking and content systems.",
      items: [
        "Direct distribution and portal strategy",
        "Real-time analytics and dashboards",
        "Lower technology cost through reuse and integration",
      ],
    },
  },
  challenges: {
    eyebrow: "Industry Challenges",
    title: "The travel stack is distributed, time-sensitive and highly interconnected",
    items: [
      {
        title: "Fragmented supplier connectivity",
        text: "Multiple supplier feeds, API styles and industry standards make aggregation complex and fragile.",
      },
      {
        title: "Booking and content alignment",
        text: "Travel portals must keep booking engines, CMS content and inventory states synchronized across channels.",
      },
      {
        title: "Dynamic business rules",
        text: "Pricing, packaging, commissions and fulfillment logic change often and need rules-based control instead of hard-coded flows.",
      },
    ],
  },
  systems: {
    eyebrow: "Enterprise Systems We Build & Modernize",
    title: "Travel platforms that connect reservation, content, automation and inventory",
    lead:
      "Retain the legacy keywords and product references that already carry authority, but present them as part of an enterprise engineering platform story.",
    items: [
      {
        title: "DXchange",
        text: "OTA-compliant XML web services middleware for bundling requests, consuming supplier data and exposing inventory through secure web services.",
        href: "/dxchange",
        linkLabel: "Explore DXchange",
      },
      {
        title: "CMS",
        text: "Do-it-yourself content management for travel portals, destination sites and multilingual web publishing.",
        href: "/cms",
        linkLabel: "Explore CMS",
      },
      {
        title: "Booking Engine",
        text: "Online booking workflows that keep reservations, channels and fulfillment aligned.",
        href: "/it-consulting-services",
        linkLabel: "See solutions",
      },
      {
        title: "Business Rules Engine",
        text: "Control distribution, pricing and workflow logic without rewrites each time the business changes.",
        href: "/biz-rule-engine",
        linkLabel: "See BRMS",
      },
    ],
  },
  modernization: {
    eyebrow: "Legacy Modernization Opportunities",
    title: "Preserve legacy travel systems while improving integration, scalability and control",
    lead:
      "The page should explicitly show how existing travel applications can be modernized rather than replaced.",
    paragraphs: [
      "DXchange enables organizations to bundle XML requests to multiple suppliers in a single request, aggregate and consume data from suppliers, and connect heterogeneous systems across .NET and Java platforms. That architecture supports incremental modernization instead of an all-at-once platform swap.",
      "The CMS travel portal, destination portal and booking-related modules demonstrate how legacy content and booking logic can be preserved while becoming easier to update, repurpose and extend for new channels.",
    ],
    callout: {
      title: "Modernization priorities",
      items: [
        "Platform-agnostic integration",
        "Reuse of legacy applications",
        "Faster deployment with lower operational risk",
        "Compatibility with OTA and TTI standards",
      ],
    },
  },
  ai: {
    eyebrow: "AI Enablement Opportunities",
    title: "AI should enhance the travel stack, not replace the travel story",
    lead:
      "Introduce AI as an intelligence layer on top of existing travel systems, not as a substitute for operational expertise.",
    items: [
      {
        title: "Demand forecasting and pricing",
        text: "Use AI to support inventory decisions, dynamic pricing and seasonal demand planning.",
      },
      {
        title: "Conversational booking and support",
        text: "Layer AI agents onto existing portals so customers and agents can find, compare and book faster.",
      },
      {
        title: "Revenue and fraud analytics",
        text: "Use pattern detection and recommendation systems to improve conversion while reducing fraud risk.",
      },
    ],
  },
  experience: {
    eyebrow: "Our Experience in This Industry",
    title: "Decades of travel technology and enterprise delivery depth",
    paragraphs: [
      "Leveraging years of software development experience and domain expertise in travel, we have delivered configurable solutions for travel vendors, destinations, associations, agents, portals, aggregators and consolidators. The work includes real-time analytics, content automation and workflow-driven integration across the travel stack.",
      "DXchange was nominated as one of the Top 100 IT Innovations for 2007 by Nasscom, India. That kind of proof matters because it shows the company has built not just generic software, but travel platforms with recognition and staying power.",
    ],
    callout: {
      title: "Evidence to preserve",
      items: [
        "NASSCOM-recognized middleware innovation",
        "OTA-compliant architecture",
        "Travel portal and CMS history",
        "Industry terminology already ranking in search",
      ],
    },
  },
  relatedSolutions: [
    { label: "IT Consulting Services", href: "/it-consulting-services", text: "Enterprise modernization and delivery planning." },
    { label: "DXchange", href: "/dxchange", text: "API management and travel data exchange." },
    { label: "CMS", href: "/cms", text: "Travel content and portal management." },
    { label: "BizRuleEngine", href: "/biz-rule-engine", text: "Rules-driven travel workflow automation." },
  ],
  why: {
    eyebrow: "Why Avani Cimcon",
    title: "A travel partner that understands systems, suppliers and long-term support",
    lead:
      "The enterprise story needs to stay anchored in evidence: 1992 heritage, global delivery, long-term partnerships and platforms that were designed to work in real operational conditions.",
    items: [
      {
        title: "Enterprise software engineering",
        text: "We combine domain knowledge, architecture and delivery discipline instead of relying on generic tourism marketing language.",
      },
      {
        title: "Preserved search intent",
        text: "We keep travel solutions, travel management solutions and related long-tail keywords intact while modernizing the story.",
      },
      {
        title: "Integration-first mindset",
        text: "We help clients reuse legacy systems, connect suppliers and modernize the stack without breaking what already works.",
      },
      {
        title: "AI with operational realism",
        text: "AI is introduced as an enhancer for pricing, booking, analytics and support workflows.",
      },
    ],
  },
  cta: {
    eyebrow: "Talk to Us",
    title: "Plan the next phase of your travel modernization program",
    text:
      "Use the existing travel platform as the foundation for safer modernization, stronger distribution and practical AI enablement.",
    primary: { label: "Contact Us", href: "/contact-us" },
    secondary: { label: "Explore Enterprise AI", href: "/enterprise-ai" },
  },
};

class TravelAndHospitality extends Component {
  render() {
    ReactGA.initialize("UA-5235714-2");
    ReactGA.pageview(window.location.pathname + window.location.search);

    return <IndustryVerticalPage {...page} />;
  }
}

export default TravelAndHospitality;
